import {Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { CommonModule } from '@angular/common';
import {FormFieldModule} from "../../../core/component/form-field/form-field.module";
import {SelectFilterV2Component} from "../../../../components/common/select-filter-v2/select-filter-v2.component";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {SelectElementV2} from "../../../../components/common/select/selectElementV2";
import {ButtonModule} from "../../../core/component/button/button.module";

@Component({
  selector: 'app-video-recorder',
  standalone: true,
  imports: [CommonModule, FormFieldModule, SelectFilterV2Component, ButtonModule],
  templateUrl: './video-recorder.component.html',
  styles: [`
    video { width: 100%; max-width: 600px; }
    button { margin-right: 10px; }
    select { margin-bottom: 10px; }
  `]
})
export class VideoRecorderComponent implements OnInit, OnDestroy {
  @ViewChild('liveVideo') liveVideo!: ElementRef<HTMLVideoElement>;
  @Output() recordedEvent: EventEmitter<Blob> = new EventEmitter();

  videoDeviceElements: SelectElementV2<MediaDeviceInfo>[] = [];
  private mediaStream!: MediaStream;
  private mediaRecorder!: MediaRecorder;
  private recordedChunks: Blob[] = [];

  elapsedTime: number = 0;
  isRecording: boolean = false;
  private timerInterval: any;

  formGroup: FormGroup;

  constructor(private formBuilder: FormBuilder) {
    this.formGroup = this.formBuilder.group({
      deviceId: new FormControl(undefined, Validators.required),
    })
  }

  async ngOnInit() {
    // Récupérer la liste des caméras disponibles
    await this.getVideoDevices();
    // Si au moins une caméra est disponible, démarrez le flux avec la première par défaut
    if (this.videoDeviceElements.length > 0) {
      this.formGroup.patchValue({
        deviceId: this.videoDeviceElements[0].value
      })
      await this.startStream(this.formGroup.value.deviceId);
    }
  }

  ngOnDestroy() {
    clearInterval(this.timerInterval);
  }

  async getVideoDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.videoDeviceElements = devices
          .filter(device => device.kind === 'videoinput')
          .map(value => new SelectElementV2<MediaDeviceInfo>(value.deviceId, value.label, value));
    } catch (error) {
      console.error('Erreur lors de la récupération des périphériques vidéo', error);
    }
  }

  async startStream(deviceId: string) {
    // Arrêter les pistes du flux précédent le cas échéant
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { ideal: deviceId } }
      });
      this.liveVideo.nativeElement.srcObject = this.mediaStream;
      this.setupMediaRecorder();
    } catch (error) {
      console.error('Erreur lors de l’accès à la caméra', error);
    }
  }

  setupMediaRecorder() {
    if (this.mediaStream) {
      this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: 'video/webm' });
      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      this.mediaRecorder.onstop = () => {
        this.recordedEvent.emit(new Blob(this.recordedChunks, { type: 'video/webm' }));
        this.recordedChunks = [];
      };
    }
  }

  onCameraChange() {
    const selectedDeviceId = this.formGroup.value.deviceId;
    if (selectedDeviceId) {
      //       // Démarrer le flux avec le nouveau deviceId sélectionné
      this.startStream(selectedDeviceId);
    }
  }

  startRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'inactive') {
      this.recordedChunks = [];
      this.mediaRecorder.start();

      this.isRecording = true;
      this.elapsedTime = 0;
      // Démarrer le timer : incrémenter elapsedTime chaque seconde
      this.timerInterval = setInterval(() => {
        this.elapsedTime++;
      }, 1000);
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
      this.isRecording = false;
      clearInterval(this.timerInterval);
    }
  }

  get formattedElapsedTime(): string {
    const minutes = Math.floor(this.elapsedTime / 60);
    const seconds = this.elapsedTime % 60;
    return (minutes < 10 ? '0' + minutes : minutes) + ':' + (seconds < 10 ? '0' + seconds : seconds);
  }
}
